import type { ReactNode } from 'react'
import { Card, Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { formatMoneyValue, money } from '@/shared/lib/money'

export interface StatementRow {
  key: string
  code: string | null
  label: ReactNode
  amountMinor: number
  emphasis?: boolean
}

export interface StatementSectionProps {
  title: string
  rows: readonly StatementRow[]
  totalLabel: string
  totalMinor: number | undefined
  currency: string
  loading: boolean
  emptyText: string
}

export function StatementSection({
  title,
  rows,
  totalLabel,
  totalMinor,
  currency,
  loading,
  emptyText,
}: StatementSectionProps) {
  const { t } = useTranslation()

  const columns: ColumnsType<StatementRow> = [
    {
      key: 'item',
      title: t('finance.statement.item'),
      render: (_, row) => (
        <Space size={6} wrap>
          {row.code ? <Typography.Text code>{row.code}</Typography.Text> : null}
          <Typography.Text strong={row.emphasis}>{row.label}</Typography.Text>
        </Space>
      ),
    },
    {
      key: 'amount',
      align: 'right',
      width: 200,
      title: t('finance.statement.amount'),
      render: (_, row) => (
        <Typography.Text strong={row.emphasis} type={row.amountMinor < 0 ? 'danger' : undefined}>
          {formatMoneyValue(money(row.amountMinor, currency))}
        </Typography.Text>
      ),
    },
  ]

  return (
    <Card size="small" title={title} style={{ marginBottom: 16 }}>
      <Table<StatementRow>
        rowKey="key"
        columns={columns}
        dataSource={[...rows]}
        loading={loading}
        pagination={false}
        size="small"
        locale={{ emptyText }}
        summary={() =>
          totalMinor === undefined ? null : (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <Typography.Text strong>{totalLabel}</Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Typography.Text strong type={totalMinor < 0 ? 'danger' : undefined}>
                  {formatMoneyValue(money(totalMinor, currency))}
                </Typography.Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )
        }
      />
    </Card>
  )
}
